/**
 * @fileoverview useToast - Toast Notification Hook
 *
 * Provides access to the unified toast notification system.
 * This hook wraps the ToastContext to provide type-safe toast functions.
 *
 * @module hooks/useToast
 * @see {@link contexts/toastContext} for the provider implementation
 * @see {@link lib/toast-unified} for the direct toast API
 *
 * @example
 * // Basic usage
 * const { toastSuccess, toastError } = useToast();
 * toastSuccess('Data saved');
 * toastError('Error saving', { description: 'Check your connection' });
 *
 * @example
 * // With undo action
 * const { toastWithUndo } = useToast();
 * toastWithUndo('Item deleted', () => restoreItem());
 *
 * @example
 * // Promise-based toast
 * const { toastPromise } = useToast();
 * toastPromise(saveData(), {
 *   loading: 'Saving...',
 *   success: 'Saved successfully!',
 *   error: 'Error saving'
 * });
 *
 * @example
 * // Persistent toast with manual dismissal
 * const { toastPersistent, dismissToast } = useToast();
 * const id = toastPersistent('Processing...', {
 *   label: 'Cancel',
 *   onClick: () => cancelOperation()
 * });
 * // Later: dismissToast(id);
 *
 * @example
 * // Loading toast with dismiss function
 * const { toastLoading } = useToast();
 * const dismiss = toastLoading('Carregando dados...');
 * await fetchData();
 * dismiss();
 */

import { useContext } from 'react';

import { ToastContext, ToastContextValue } from 'contexts/toastContext';

/**
 * Hook to access toast notification functions.
 *
 * Provides access to all toast notification methods:
 *
 * **Simple notifications:**
 * - `toastSuccess` - Positive actions completed (green)
 * - `toastError` - Failed operations or validation errors (red, longer duration)
 * - `toastWarning` - Potential issues or confirmations needed (yellow/orange)
 * - `toastInfo` - Neutral information or tips (blue)
 *
 * **Advanced patterns:**
 * - `toastWithUndo` - Toast with "Desfazer" (Undo) button
 * - `toastPersistent` - Toast that never auto-dismisses
 * - `toastLoading` - Loading toast with dismiss function
 * - `toastPromise` - Promise-based toast with loading/success/error states
 *
 * **Utilities:**
 * - `dismissToast` - Dismiss a specific toast by ID
 * - `dismissAllToasts` - Dismiss all active toasts
 *
 * @returns {ToastContextValue} Toast notification functions
 * @throws {Error} If used outside of ToastProvider
 */
export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default useToast;
